import React, { Fragment } from 'react'
import { Redirect } from 'react-router'
import { FormattedMessage } from 'react-intl'
import withApi, { type WithApiInjectedProps } from 'legacy/common/withApi'
import SubNav from 'legacy/components/Nav/SubNav'
import SubNavItem from 'legacy/components/Nav/SubNavItem'
import RetailerRoute, { type RetailerRouteComponentProps } from 'legacy/components/RetailerRoute'
import DocumentTitle from 'legacy/components/DocumentTitle'
import PageContainer from 'legacy/components/Containers/PageContainer'
import { AsyncStatus, type ScopedRouteParams } from 'legacy/common/types'
import { PRODUCT_EDIT_URL, PRODUCT_EDIT_DETAILS_URL } from 'legacy/common/routes'
import LoadingDots from 'legacy/components/LoadingDots'
import EmptyView from 'legacy/components/EmptyView'
import RetailerSwitch from 'legacy/components/RetailerSwitch'
import ProductContainer from 'legacy/routes/catalog/products/product/ProductContainer'
import ProductLocations from './ProductLocations'
import ProductAisles from './ProductAisles'
import ProductDetailsForm from './ProductDetailsForm'
import EditProductBreadcrumbs from 'legacy/routes/catalog/breadcrumbs/EditProductBreadcrumbs'
import AppHeader from 'legacy/components/AppHeader'
import styled from '@emotion/styled'
import { colors } from 'ic-snacks'
import useAccessControl from 'legacy/components/AccessControl/useAccessControl'
import { Flipper } from 'legacy/contexts/flipper/FlipperContext'
import { transformFormValuesToRequestBody } from 'legacy/routes/catalog/products/product/productUtils'

const StyledSubNav = styled(SubNav)`
  border-right: 1px solid ${colors.GRAY_88};
`

interface RouteParams extends ScopedRouteParams {
  product_id: string
}

interface Props extends WithApiInjectedProps, RetailerRouteComponentProps<RouteParams> {
  showAisles: boolean
}

class EditProduct extends React.Component<Props> {
  renderError() {
    return (
      <div style={{ height: '600px', width: '100%', paddingTop: '80px' }}>
        <EmptyView
          size="large"
          iconName="warning"
          title={<FormattedMessage id="catalog.product.fetchFailed.title" />}
          body={<FormattedMessage id="catalog.product.fetchFailed.message" />}
        />
      </div>
    )
  }

  render() {
    const {
      url,
      history: {
        location: { search },
      },
      match: {
        params: { partner_id, warehouse_id, product_id },
      },
      showAisles,
    } = this.props

    return (
      <ProductContainer {...this.props.match.params}>
        {({
          product: { data: productData, status },
          brands: { data: brandsData },
          onUpdate,
          availabilityLoading,
          updateProductStatus,
          availableItemsCount,
        }) => (
          <Fragment>
            {status === AsyncStatus.Done && productData && (
              <DocumentTitle title={productData.name} titleId="catalog.title" />
            )}
            <AppHeader title={<EditProductBreadcrumbs productData={productData} />} />
            <PageContainer
              style={{
                display: 'flex',
                flexFlow: 'row nowrap',
                overflowY: 'scroll',
              }}
            >
              <StyledSubNav>
                <SubNavItem exact to={url('productEditDetails', { product_id })}>
                  <FormattedMessage id="catalog.product.details" />
                </SubNavItem>
                {showAisles && (
                  <SubNavItem exact to={url('productEditAisles', { product_id })}>
                    <FormattedMessage id="catalog.product.aisles" />
                  </SubNavItem>
                )}
                <SubNavItem exact to={url('productEditLocations', { product_id })}>
                  <FormattedMessage id="catalog.product.locations" />
                </SubNavItem>
              </StyledSubNav>
              {status === AsyncStatus.Fail && this.renderError()}
              {status === AsyncStatus.Busy && (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <LoadingDots centered />
                </div>
              )}
              {productData && brandsData && (
                <RetailerSwitch>
                  <RetailerRoute
                    route="productEditDetails"
                    render={() => (
                      <ProductDetailsForm
                        product={productData}
                        brands={brandsData}
                        onSubmit={onUpdate}
                        isSubmitting={updateProductStatus === AsyncStatus.Busy}
                      />
                    )}
                  />
                  {showAisles && (
                    <RetailerRoute
                      route="productEditAisles"
                      render={() =>
                        availabilityLoading ? (
                          <LoadingDots centered />
                        ) : (
                          <ProductAisles
                            availabilityLoading={availabilityLoading}
                            partnerId={partner_id}
                            warehouseId={warehouse_id}
                            productId={product_id}
                            product={productData}
                            availableItemsCount={availableItemsCount}
                          />
                        )
                      }
                    />
                  )}
                  <RetailerRoute
                    route="productEditLocations"
                    render={() => (
                      <ProductLocations
                        partnerId={partner_id}
                        warehouseId={warehouse_id}
                        productId={product_id}
                        product={productData}
                      />
                    )}
                  />
                  <Redirect
                    from={PRODUCT_EDIT_URL}
                    to={{ pathname: PRODUCT_EDIT_DETAILS_URL, search }}
                  />
                </RetailerSwitch>
              )}
            </PageContainer>
          </Fragment>
        )}
      </ProductContainer>
    )
  }
}

/* eslint-disable react/no-multi-comp */
const withFlipper = (Component: React.ComponentClass<Props>) => {
  return (props: Props) => {
    const hasAccess = useAccessControl()
    const hideDepartmentAisle = hasAccess({
      warehouseFlippers: Flipper.CatalogHideProductAisles,
    })

    return <Component showAisles={!hideDepartmentAisle} {...props} />
  }
}

export default withApi(withFlipper(EditProduct))
